import classnames from 'classnames';
import { forwardRef, type ReactNode } from 'react';

import type { AnalyticsListOptions } from '../../../../../analytics/analyticsHandler';
import type { EcomPromotion } from '../../../../common/models/domain/ecommerce/promotion/promotion';

import CloudinaryImage from '../../../../common/components/atoms/CloudinaryImage';
import { ConditionalMultipleWrappers } from '../../../../common/components/atoms/ConditionalWrapper';
import { ItemMedicalInfo } from '../../../../common/components/atoms/MedicalInfo';
import {
    getFormattedComparativePrice,
    getFormattedPackagingInfo,
} from '../../../../common/utils/productUtils';
import MaxPromotionUsage from '../../../../ecommerce/components/MaxPromotionUsage';

interface ProductListingItemProps {
    name?: string;
    packageSizeInformation?: string;
    variantName?: string;
    image?: string;
    manufacturer?: string;
    productUrl?: string;
    additionalClassNames?: string;
    slotLeft?: ReactNode;
    slotPrice?: ReactNode;
    slotSplash?: ReactNode;
    analyticsOptions: AnalyticsListOptions | null;
    bottomRow?: ReactNode;
    handleLinkClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    comparativePrice?: number;
    comparativeText?: string;
    isNicotineProduct: boolean;
    isPharmaceutical: boolean;
    promotion?: EcomPromotion;
    isOutOfStock?: boolean;
    isALink?: boolean;
}

const ProductListingItem = forwardRef(
    <C extends React.ElementType = 'li'>(
        props: React.PolymorphicComponentPropsWithRef<C, ProductListingItemProps>,
        ref: React.PolymorphicRef<C>,
    ) => {
        const Component = props.as || 'li';

        const isALink = props.isALink || true;

        return (
            <Component ref={ref} className={classnames('Cart-item', props.additionalClassNames)}>
                <div
                    className={classnames(
                        'Cart-itemRow',
                        props.isOutOfStock && 'u-textLineThrough',
                    )}
                >
                    {props.slotLeft}
                    <div className={classnames('Cart-itemContainer', 'Cart-itemContainerWrapped')}>
                        <div className="Cart-itemRow Cart-itemRowPrimary">
                            <div
                                className={classnames(
                                    'Cart-itemWrapperImage',
                                    !!props.slotSplash && 'Cart-itemWrapperImageWithPromo',
                                )}
                            >
                                <ProductItemImage
                                    productUrl={props.productUrl}
                                    handleLinkClick={props.handleLinkClick}
                                    image={props.image}
                                    name={props.name}
                                    isALink={isALink}
                                />
                                {props.slotSplash && (
                                    <div className="Cart-itemSplash">{props.slotSplash}</div>
                                )}
                            </div>
                            <div className="Cart-itemWrapper">
                                <div className="Cart-itemWrapperProduct">
                                    <div className="Cart-itemWrapperDetail">
                                        <ProductItemInfo
                                            productUrl={props.productUrl}
                                            name={props.name}
                                            manufacturer={props.manufacturer}
                                            variantName={props.variantName}
                                            packageSizeInformation={props.packageSizeInformation}
                                            comparativePrice={props.comparativePrice}
                                            comparativeText={props.comparativeText}
                                            handleLinkClick={props.handleLinkClick}
                                            isNicotineProduct={props.isNicotineProduct}
                                            isPharmaceutical={props.isPharmaceutical}
                                            promotion={props.promotion}
                                            isALink={isALink}
                                        />
                                    </div>
                                    <div className="Cart-itemWrapperPrice">{props.slotPrice}</div>
                                </div>
                                {!!props.children && (
                                    <div className="Cart-itemRow">
                                        <div className="Cart-itemWrapperActions">
                                            {props.children}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {!!props.bottomRow && <div className="Cart-itemRow">{props.bottomRow}</div>}
            </Component>
        );
    },
);

interface ProductItemInfoProps {
    productUrl?: string;
    handleLinkClick?: React.MouseEventHandler<HTMLAnchorElement>;
    name?: React.ReactNode;
    manufacturer?: string;
    variantName?: string;
    packageSizeInformation?: string;
    comparativePrice?: number;
    comparativeText?: string;
    isNicotineProduct?: boolean;
    isPharmaceutical?: boolean;
    promotion?: EcomPromotion;
    isALink?: boolean;
}

const ProductItemInfo = ({
    productUrl,
    handleLinkClick,
    name,
    manufacturer,
    variantName,
    packageSizeInformation,
    comparativePrice,
    comparativeText,
    isNicotineProduct,
    isPharmaceutical,
    promotion,
    isALink = true,
}: ProductItemInfoProps) => {
    const packagingInfo = getFormattedPackagingInfo(variantName, packageSizeInformation);

    const comparativePriceFormatted = getFormattedComparativePrice(
        comparativePrice,
        comparativeText,
    );

    return (
        <div className="u-flexAlignSelfCenter u-textLeft">
            <ConditionalMultipleWrappers
                choices={[
                    {
                        condition: !!productUrl && isALink,
                        renderWrapper: (ch) => (
                            <a
                                href={productUrl}
                                className="Cart-itemHeading Cart-itemHeading--link"
                                style={{ whiteSpace: 'normal' }}
                                onClick={handleLinkClick}
                                data-test="cart-productitem-name"
                                tabIndex={-1}
                            >
                                {ch}
                            </a>
                        ),
                    },
                    {
                        condition: !isALink || !productUrl,
                        renderWrapper: (ch) => (
                            <div
                                className="Cart-itemHeading"
                                style={{ whiteSpace: 'normal' }}
                                data-test="cart-productitem-name"
                            >
                                {ch}
                            </div>
                        ),
                    },
                ]}
            >
                {name}
            </ConditionalMultipleWrappers>
            <p
                className="Cart-itemSubtitle Cart-itemSubtitleWrapped"
                style={{ whiteSpace: 'normal' }}
            >
                {manufacturer && (
                    <span className="u-textWeightBold">
                        {`${manufacturer}`}
                        {manufacturer && '. '}
                    </span>
                )}
                {!!packagingInfo && (
                    <span>
                        {packagingInfo}
                        {!!comparativePriceFormatted && '. '}
                    </span>
                )}
                {!!comparativePriceFormatted && <span>{comparativePriceFormatted}</span>}
                {!!promotion?.maxUseText && <MaxPromotionUsage text={promotion.maxUseText} />}
            </p>
            <ItemMedicalInfo isNicotine={isNicotineProduct} isPharmaceutical={isPharmaceutical} />
        </div>
    );
};

interface ProductItemImageProps {
    productUrl?: string;
    handleLinkClick?: React.MouseEventHandler<HTMLAnchorElement>;
    image?: string;
    name?: string;
    isALink?: boolean;
}

const ProductItemImage = ({
    productUrl,
    handleLinkClick,
    image,
    name,
    isALink = true,
}: ProductItemImageProps) => {
    return (
        <ConditionalMultipleWrappers
            choices={[
                {
                    condition: !!productUrl && isALink,
                    renderWrapper: (ch) => (
                        <a href={productUrl} className="u-block" onClick={handleLinkClick}>
                            {ch}
                        </a>
                    ),
                },
                {
                    condition: !isALink || !productUrl,
                    renderWrapper: (ch) => ch,
                },
            ]}
        >
            <CloudinaryImage
                cssClass="Cart-itemImage"
                url={image}
                name={name || ''}
                transform="e_sharpen,f_auto,fl_clip,fl_progressive,q_90,c_lpad,g_center,h_68,w_68"
                retinaTransform="e_sharpen,f_auto,fl_clip,fl_progressive,q_70,c_lpad,g_center,h_136,w_136"
                fallbackUrl="/Assets/Images/missing-image-product.png"
            />
        </ConditionalMultipleWrappers>
    );
};

export default ProductListingItem;

export const ProductListItem = {
    Image: ProductItemImage,
    Info: ProductItemInfo,
};
